import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { coachKeys } from './keys'

export type TranscriptDialogRole = 'user' | 'bot'

export type TranscriptDialogTurn = {
  role: TranscriptDialogRole
  contents: string[]
}

type VapiDialogMessage = {
  role: TranscriptDialogRole
  message: string
}

const isDialogRole = (role: unknown): role is TranscriptDialogRole =>
  role === 'user' || role === 'bot'

/**
 * Parses the transcript dialog data into an array of turns, discarding non-dialog entries.
 */
const parseTranscriptDialog = (data: unknown): TranscriptDialogTurn[] => {
  if (!Array.isArray(data)) {
    return []
  }

  const messages: VapiDialogMessage[] = []
  for (const item of data) {
    if (!item || typeof item !== 'object') {
      continue
    }
    const { role, message } = item as { role?: unknown; message?: unknown }
    if (!isDialogRole(role) || typeof message !== 'string') {
      continue
    }
    const trimmed = message.trim()
    if (!trimmed) {
      continue
    }
    messages.push({ role, message: trimmed })
  }

  const turns: TranscriptDialogTurn[] = []
  for (const message of messages) {
    const lastTurn = turns[turns.length - 1]
    if (lastTurn && lastTurn.role === message.role) {
      lastTurn.contents.push(message.message)
    } else {
      turns.push({ role: message.role, contents: [message.message] })
    }
  }
  return turns
}

type UseAssessmentTranscriptQueryData = TranscriptDialogTurn[]
type UseAssessmentTranscriptQueryKey = ReturnType<
  typeof coachKeys.assessmentTranscript
>

type UseAssessmentTranscriptQueryOptions = Partial<
  UseQueryOptions<
    UseAssessmentTranscriptQueryData,
    Error,
    UseAssessmentTranscriptQueryData,
    UseAssessmentTranscriptQueryKey
  >
>

export const useAssessmentTranscript = (
  transcriptUrl: string,
  options: UseAssessmentTranscriptQueryOptions = {}
) => {
  return useQuery<
    UseAssessmentTranscriptQueryData,
    Error,
    UseAssessmentTranscriptQueryData,
    UseAssessmentTranscriptQueryKey
  >({
    ...options,
    queryKey: coachKeys.assessmentTranscript(transcriptUrl),
    queryFn: async () => {
      const response = await fetch(transcriptUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch transcript')
      }
      return parseTranscriptDialog(await response.json())
    },
  })
}
